import React, { useRef, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import './Campaign.css';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';
import { needsTokenRefresh, refreshToken, isTokenValid } from './auth';
import { playSoundEffect } from './hooks/useSoundEffects';
import MenuBackButton from './components/MenuBackButton';
import './Tutorial.css';
import { BATTLE_OVERVIEW_MODE_STICKER_KEYS } from './battleOverview/modeStickerKeys';
import { useDismissBattleOverviewModeSticker } from './battleOverview/useDismissBattleOverviewModeSticker';

/** Set to true when the campaign story flip book should be playable again. */
const IS_CAMPAIGN_STORYBOOK_AVAILABLE = false;

/** Top selector tabs: tutorial first, then anthology, four future slots (matches standalone Tutorial.js missions). */
const CAMPAIGN_TOP_TAB_IDS = ['tutorial', 'anthology', 'future2', 'future3', 'future4', 'future5'];

const TUTORIAL_MISSION_ITEMS = [
  { id: 'tutorial1', missionId: 'chapter_1_mission_1', image: 'tutorial1.jpg', titleKey: 'tutorial.tutorial1', descriptionKey: 'tutorial.description1' },
  { id: 'tutorial2', missionId: 'chapter_1_mission_2', image: 'tutorial2.jpg', titleKey: 'tutorial.tutorial2', descriptionKey: 'tutorial.description2' },
  { id: 'tutorial3', missionId: 'chapter_1_mission_3', image: 'tutorial3.jpg', titleKey: 'tutorial.tutorial3', descriptionKey: 'tutorial.description3' },
  { id: 'tutorial4', missionId: 'chapter_1_mission_4', image: 'tutorial4.jpg', titleKey: 'tutorial.tutorial4', descriptionKey: 'tutorial.description4' },
];

function Campaign({ user, onLogout }) {
  const { t } = useTranslation();
  useDismissBattleOverviewModeSticker(user, BATTLE_OVERVIEW_MODE_STICKER_KEYS.anthology);
  const navigate = useNavigate();
  const location = useLocation();
  const flipBookRef = useRef(null);
  const pageTurnAudioRef = useRef(null);
  const [campaignProgress, setCampaignProgress] = useState(null);
  const [isMissionCompleted, setIsMissionCompleted] = useState(false);
  const [isMission2Completed, setIsMission2Completed] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [remnantsStrategies, setRemnantsStrategies] = useState([]);
  const [selectedRemnantsStrategyId, setSelectedRemnantsStrategyId] = useState('');
  const [startingReliveMission, setStartingReliveMission] = useState('');
  const [isRelivePickerActive, setIsRelivePickerActive] = useState(false);
  /** Book page indices that show relive pickers: 3 = mission 5, 5 = mission 6 */
  const [reliveOverlaySlot, setReliveOverlaySlot] = useState(null);
  const [reliveOverlayOpaque, setReliveOverlayOpaque] = useState(false);
  const reliveFadeInRafRef = useRef(null);
  const [selectedTopTab, setSelectedTopTab] = useState(() =>
    location.state?.selectedTopTab === 'tutorial' ? 'tutorial' : 'anthology'
  );

  useEffect(() => {
    if (location.state?.selectedTopTab === 'tutorial') {
      setSelectedTopTab('tutorial');
    }
  }, [location.state?.selectedTopTab]);

  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  // Tab-specific campaign music: tutorial vs human-camp (anthology) sections
  const campaignBackgroundMusic =
    selectedTopTab === 'tutorial' ? '/music/tutorial4.mp3' : '/music/campaign_menu.mp3';
  useMusic(campaignBackgroundMusic);

  const username = typeof user === 'string' ? user : user?.username;
  const isDevTesterPlayer1 = username === 'player1';
  const isStorybookAvailable = IS_CAMPAIGN_STORYBOOK_AVAILABLE || isDevTesterPlayer1;

  // Compare mission IDs to determine if a mission is completed
  const compareMissionIds = (mission1, mission2) => {
    // Parse mission IDs: "chapter_X_mission_Y"
    const parseMission = (missionId) => {
      if (!missionId) return { chapter: 0, mission: 0, original: '' };
      const match = missionId.match(/chapter_(\d+)_mission_(\d+)/);
      if (!match) {
        return { chapter: 0, mission: 0, original: missionId };
      }
      return {
        chapter: parseInt(match[1], 10),
        mission: parseInt(match[2], 10),
        original: missionId
      };
    };
    
    const m1 = parseMission(mission1);
    const m2 = parseMission(mission2);
    
    // Compare by chapter first, then by mission number
    if (m1.chapter !== m2.chapter) {
      return m1.chapter - m2.chapter;
    }
    if (m1.mission !== m2.mission) {
      return m1.mission - m2.mission;
    }
    
    return 0; // Same mission
  };

  const isTutorialCampaignMissionCompleted = (missionId) => {
    if (!missionId || !campaignProgress) return false;
    return compareMissionIds(campaignProgress, missionId) >= 0;
  };

  const handleTutorialMissionClick = (item) => {
    if (!item.missionId) {
      throw new Error('Tutorial mission item requires missionId');
    }
    playSoundEffect('/sounds/click.mp3');
    navigate(`/campaign-battle/${item.missionId}`);
  };

  const ensureValidToken = async () => {
    let token = localStorage.getItem('token');
    const refreshTokenValue = localStorage.getItem('refreshToken');

    if (token && refreshTokenValue) {
      if (!isTokenValid(token) || needsTokenRefresh(token)) {
        token = await refreshToken(refreshTokenValue);
        localStorage.setItem('token', token);
      }
    }

    if (!token) {
      throw new Error('No authentication token available');
    }

    return token;
  };

  // Fetch campaign progress on mount
  useEffect(() => {
    const fetchCampaignProgress = async () => {
      try {
        const userId = parseInt(localStorage.getItem('userId'));
        if (!userId) return;

        const token = localStorage.getItem('token');
        const apiUrl = API_URL;
        const response = await fetch(`${apiUrl}/campaign/progress`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCampaignProgress(data.latest_campaign || null);
          // Check if chapter_1_mission_1 is completed (latest_campaign >= chapter_1_mission_1)
          const requiredMission = 'chapter_1_mission_1';
          const requiredMission2 = 'chapter_1_mission_2';
          if (data.latest_campaign) {
            const comparison = compareMissionIds(data.latest_campaign, requiredMission);
            setIsMissionCompleted(comparison >= 0); // Completed if latest >= required
            const comparison2 = compareMissionIds(data.latest_campaign, requiredMission2);
            setIsMission2Completed(comparison2 >= 0);
          } else {
            setIsMissionCompleted(false);
            setIsMission2Completed(false);
          }
        }
      } catch (error) {
        console.error('Error fetching campaign progress:', error);
      }
    };

    fetchCampaignProgress();
  }, []);

  useEffect(() => {
    const fetchRemnantsStrategies = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/my-strategies`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load strategies (${response.status})`);
        }

        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error('Invalid strategies response');
        }

        const filtered = data.filter((strategy) => strategy?.faction_name === 'Remnants of Humanity');
        setRemnantsStrategies(filtered);

        if (filtered.length > 0) {
          setSelectedRemnantsStrategyId(String(filtered[0].id));
        } else {
          setSelectedRemnantsStrategyId('');
        }
      } catch (error) {
        console.error('Error fetching Remnants strategies:', error);
        setRemnantsStrategies([]);
        setSelectedRemnantsStrategyId('');
      }
    };

    fetchRemnantsStrategies();
  }, []);

  const handleReliveAdventure = async (missionId) => {
    if (!selectedRemnantsStrategyId || startingReliveMission) {
      return;
    }

    if (!missionId) {
      alert('Missing mission id for relive mission');
      return;
    }

    setStartingReliveMission(missionId);
    try {
      const token = await ensureValidToken();
      const selectedLanguage = (localStorage.getItem('i18nextLng') || 'en-us').toLowerCase();

      const response = await fetch(`${API_URL}/campaign/start-mission?lang=${encodeURIComponent(selectedLanguage)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          missionId,
          userId: parseInt(localStorage.getItem('userId'), 10),
          language: selectedLanguage,
          strategyId: parseInt(selectedRemnantsStrategyId, 10),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to start relive mission');
      }

      navigate(`/battle/${data.gameId}`, {
        state: {
          strategyId: selectedRemnantsStrategyId,
          userId: localStorage.getItem('userId'),
          token: localStorage.getItem('token'),
          isBotGame: true,
          gameState: data.gameState,
          yourTurn: data.gameState?.turn === String(localStorage.getItem('userId')),
          missionId,
          isCampaign: true,
        },
      });
    } catch (error) {
      console.error('Error starting relive mission:', error);
      alert(error.message || 'Failed to start relive mission');
    } finally {
      setStartingReliveMission('');
    }
  };

  // Initialize page turn audio
  useEffect(() => {
    pageTurnAudioRef.current = new Audio('/sounds/turn_page.mp3');
    pageTurnAudioRef.current.volume = 0.5;
    
    return () => {
      if (pageTurnAudioRef.current) {
        pageTurnAudioRef.current.pause();
        pageTurnAudioRef.current = null;
      }
    };
  }, []);

  const cancelReliveFadeInRaf = () => {
    if (reliveFadeInRafRef.current !== null) {
      cancelAnimationFrame(reliveFadeInRafRef.current);
      reliveFadeInRafRef.current = null;
    }
  };

  const scheduleReliveOverlayFadeIn = () => {
    cancelReliveFadeInRaf();
    setReliveOverlayOpaque(false);
    reliveFadeInRafRef.current = requestAnimationFrame(() => {
      reliveFadeInRafRef.current = requestAnimationFrame(() => {
        reliveFadeInRafRef.current = null;
        setReliveOverlayOpaque(true);
      });
    });
  };

  useEffect(() => {
    return () => cancelReliveFadeInRaf();
  }, []);

  // Handle page flip start - check if flip should be prevented and play sound
  const handleFlipStart = (e) => {
    let idxAtFlip = currentPageIndex;
    if (flipBookRef.current && flipBookRef.current.pageFlip) {
      try {
        const pf = flipBookRef.current.pageFlip();
        if (pf && typeof pf.getCurrentPageIndex === 'function') {
          idxAtFlip = pf.getCurrentPageIndex();
        }
      } catch (_) {
        /* keep idxAtFlip from state */
      }
    }
    if (idxAtFlip === 3 || idxAtFlip === 5) {
      flushSync(() => {
        cancelReliveFadeInRaf();
        setReliveOverlaySlot(null);
        setReliveOverlayOpaque(false);
      });
    }

    // If on page 3 (index 3) and mission not completed, prevent forward flip
    if (!isMissionCompleted && currentPageIndex === 3) {
      // Try to prevent the flip by checking if it's a forward flip
      if (flipBookRef.current && flipBookRef.current.pageFlip) {
        try {
          const pageFlip = flipBookRef.current.pageFlip();
          // Cancel the flip if we're trying to go forward
          // Note: We can't easily detect direction in onFlipStart, so we'll handle it in onFlip
        } catch (error) {
          // Continue with normal flip
        }
      }
    }
    
    if (pageTurnAudioRef.current) {
      pageTurnAudioRef.current.currentTime = 0;
      pageTurnAudioRef.current.play().catch(err => {
        console.log('Could not play page turn sound:', err);
      });
    }
  };

  // Handle page flip complete
  const handleFlip = (e) => {
    // Track current page index - e.data contains page information
    let newPageIndex = currentPageIndex;
    if (flipBookRef.current && flipBookRef.current.pageFlip) {
      try {
        const pageFlip = flipBookRef.current.pageFlip();
        if (pageFlip && typeof pageFlip.getCurrentPageIndex === 'function') {
          newPageIndex = pageFlip.getCurrentPageIndex();
        }
      } catch (error) {
        // Fallback: try to get from event data
        if (e && e.data && typeof e.data === 'number') {
          newPageIndex = e.data;
        }
      }
    }
    
    // If mission is not completed, allow one extra spread (page 5), but block progressing beyond it.
    if (!isMissionCompleted && newPageIndex > 5 && currentPageIndex >= 5) {
      setReliveOverlaySlot(5);
      setReliveOverlayOpaque(true);
      // Immediately flip back to page 3 - use setTimeout to ensure flip animation has started
      setTimeout(() => {
        if (flipBookRef.current && flipBookRef.current.pageFlip) {
          try {
            const pageFlip = flipBookRef.current.pageFlip();
            if (pageFlip && typeof pageFlip.flipPrev === 'function') {
              pageFlip.flipPrev();
            }
          } catch (error) {
            console.log('Could not flip back:', error);
          }
        }
      }, 50);
      // Don't update the page index - stay on the currently allowed page
      return;
    }

    if (newPageIndex === 3) {
      setReliveOverlaySlot(3);
      scheduleReliveOverlayFadeIn();
    } else if (newPageIndex === 5) {
      setReliveOverlaySlot(5);
      scheduleReliveOverlayFadeIn();
    } else {
      cancelReliveFadeInRaf();
      setReliveOverlaySlot(null);
      setReliveOverlayOpaque(false);
    }

    setCurrentPageIndex(newPageIndex || 0);
  };

  // Initialize page index on mount and when flipBook ref is populated after first paint
  useEffect(() => {
    const syncFromBook = () => {
      if (!flipBookRef.current || !flipBookRef.current.pageFlip) {
        return;
      }
      try {
        const pageFlip = flipBookRef.current.pageFlip();
        if (pageFlip && typeof pageFlip.getCurrentPageIndex === 'function') {
          const pageIndex = pageFlip.getCurrentPageIndex();
          const idx = pageIndex || 0;
          setCurrentPageIndex(idx);
          if (idx === 3) {
            setReliveOverlaySlot(3);
            scheduleReliveOverlayFadeIn();
          } else if (idx === 5) {
            setReliveOverlaySlot(5);
            scheduleReliveOverlayFadeIn();
          } else {
            cancelReliveFadeInRaf();
            setReliveOverlaySlot(null);
            setReliveOverlayOpaque(false);
          }
        }
      } catch (error) {
        console.log('Could not get initial page index:', error);
      }
    };

    syncFromBook();
    const t = requestAnimationFrame(() => syncFromBook());
    return () => cancelAnimationFrame(t);
  }, [isStorybookAvailable]);

  // Prevent forward flip from page 3 to page 4 when mission not completed
  useEffect(() => {
    const bookContainer = document.querySelector('.campaign-flip-book');
    if (!bookContainer) return;

    const handleClick = (e) => {
      // Allow interaction with relive controls on page 3/4 without triggering book turn guards.
      if (e.target && e.target.closest && (e.target.closest('.page3-relive-controls') || e.target.closest('.page4-relive-controls'))) {
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
        return;
      }

      // Unmount relive UI synchronously before HTMLFlipBook runs the page-turn (capture phase).
      let bookPageIndex = currentPageIndex;
      if (flipBookRef.current && flipBookRef.current.pageFlip) {
        try {
          const pageFlip = flipBookRef.current.pageFlip();
          if (pageFlip && typeof pageFlip.getCurrentPageIndex === 'function') {
            bookPageIndex = pageFlip.getCurrentPageIndex();
          }
        } catch (error) {
          /* use state index */
        }
      }
      if (bookPageIndex === 3 || bookPageIndex === 5) {
        const target = e.target;
        const isPageEdge = target.closest('.stf__item') ||
          target.closest('.stf__page') ||
          target.closest('.stf__block') ||
          target.classList.contains('stf__item') ||
          target.classList.contains('stf__page') ||
          target.classList.contains('stf__block');
        const containerRect = bookContainer.getBoundingClientRect();
        const cx = typeof e.clientX === 'number'
          ? e.clientX
          : (e.touches && e.touches[0] ? e.touches[0].clientX : null);
        if (isPageEdge && cx !== null && containerRect.width > 0) {
          const clickX = cx - containerRect.left;
          const w = containerRect.width;
          const inFlipZone = clickX < w * 0.35 || clickX > w * 0.65;
          if (inFlipZone) {
            flushSync(() => {
              cancelReliveFadeInRaf();
              setReliveOverlaySlot(null);
              setReliveOverlayOpaque(false);
            });
          }
        }
      }

      // Only prevent forward progress past page 5 when mission is not completed
      if (!isMissionCompleted) {
        // Get the current page index at click time to be sure
        let currentPage = currentPageIndex;
        if (flipBookRef.current && flipBookRef.current.pageFlip) {
          try {
            const pageFlip = flipBookRef.current.pageFlip();
            if (pageFlip && typeof pageFlip.getCurrentPageIndex === 'function') {
              currentPage = pageFlip.getCurrentPageIndex();
            }
          } catch (error) {
            // Use state value
          }
        }

        // If on page 5+, check if this is a click that would cause an additional forward flip
        if (currentPage >= 5) {
          // Check if clicking on page edge elements (where HTMLFlipBook detects flips)
          const target = e.target;
          const isPageEdge = target.closest('.stf__item') || 
                           target.closest('.stf__page') || 
                           target.closest('.stf__block') ||
                           target.classList.contains('stf__item') ||
                           target.classList.contains('stf__page') ||
                           target.classList.contains('stf__block');
          
          // Also check if clicking on the right side of the book container
          const containerRect = bookContainer.getBoundingClientRect();
          const clickX = e.clientX - containerRect.left;
          const containerWidth = containerRect.width;
          const isRightEdge = clickX > containerWidth * 0.7; // Right 30% of book
          
          // If clicking on page edge elements on the right side, prevent forward flip
          if (isPageEdge && isRightEdge) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }
        }
      }
    };

    // Use capture phase to intercept before HTMLFlipBook handles it
    bookContainer.addEventListener('mousedown', handleClick, true);
    bookContainer.addEventListener('touchstart', handleClick, true);

    return () => {
      bookContainer.removeEventListener('mousedown', handleClick, true);
      bookContainer.removeEventListener('touchstart', handleClick, true);
    };
  }, [isMissionCompleted, currentPageIndex]);


  // Campaign pages data - you can customize this
  const campaignPages = [
    {
      title: t('campaign.page1.title', 'Campaign - Chapter 1'),
      content: t('campaign.page1.content', 'Welcome to the Campaign. This is the beginning of your journey.')
    },
    {
      title: t('campaign.page2.title', 'Chapter 2'),
      content: t('campaign.page2.content', 'Continue your adventure...')
    },
    {
      title: t('campaign.page3.title', 'Chapter 3'),
      content: t('campaign.page3.content', 'The story continues as you progress through the campaign.')
    },
    {
      title: t('campaign.page4.title', 'Chapter 4'),
      content: t('campaign.page4.content', 'New challenges await you in this chapter.')
    },
    {
      title: t('campaign.page5.title', 'Chapter 5'),
      content: t('campaign.page5.content', 'Your skills are tested as you face stronger opponents.')
    },
    {
      title: t('campaign.page6.title', 'Chapter 6'),
      content: t('campaign.page6.content', 'The plot thickens as you uncover more secrets.')
    },
    {
      title: t('campaign.page7.title', 'Chapter 7'),
      content: t('campaign.page7.content', 'You discover new territories and allies.')
    },
    {
      title: t('campaign.page8.title', 'Chapter 8'),
      content: t('campaign.page8.content', 'The stakes are raised as you approach the midpoint.')
    },
    {
      title: t('campaign.page9.title', 'Chapter 9'),
      content: t('campaign.page9.content', 'Your journey takes an unexpected turn.')
    },
    {
      title: t('campaign.page10.title', 'Chapter 10'),
      content: t('campaign.page10.content', 'Halfway through your campaign, new opportunities arise.')
    },
    {
      title: t('campaign.page11.title', 'Chapter 11'),
      content: t('campaign.page11.content', 'You face difficult decisions that will shape your path.')
    },
    {
      title: t('campaign.page12.title', 'Chapter 12'),
      content: t('campaign.page12.content', 'The challenges become more complex and rewarding.')
    },
    {
      title: t('campaign.page13.title', 'Chapter 13'),
      content: t('campaign.page13.content', 'You learn valuable lessons from your experiences.')
    },
    {
      title: t('campaign.page14.title', 'Chapter 14'),
      content: t('campaign.page14.content', 'The story reaches a critical point in your journey.')
    },
    {
      title: t('campaign.page15.title', 'Chapter 15'),
      content: t('campaign.page15.content', 'You prepare for the final stages of the campaign.')
    },
    {
      title: t('campaign.page16.title', 'Chapter 16'),
      content: t('campaign.page16.content', 'The climax approaches as tensions rise.')
    },
    {
      title: t('campaign.page17.title', 'Chapter 17'),
      content: t('campaign.page17.content', 'You face your greatest challenges yet.')
    },
    {
      title: t('campaign.page18.title', 'Chapter 18'),
      content: t('campaign.page18.content', 'The end is near, but the journey is not over.')
    },
    {
      title: t('campaign.page19.title', 'Chapter 19'),
      content: t('campaign.page19.content', 'You stand on the brink of completing your mission.')
    },
    {
      title: t('campaign.page20.title', 'Chapter 20'),
      content: t('campaign.page20.content', 'The final chapter awaits. Your legacy is about to be written.')
    }
  ];

  return (
    <div className="campaign-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="campaign-fullscreen-background"
        style={{
          backgroundImage:
            selectedTopTab === 'tutorial'
              ? 'url(/images/tutorial.jpg)'
              : 'url(/images/human_camp.jpg)',
        }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`campaign-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="campaign-container">
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
          </div>

          <MenuBackButton to="/battleoverview" />

      {/* Campaign book selector row: tutorial, anthology, future slots */}
      <div className="campaign-book-row" role="tablist" aria-label={t('campaign.topTabListLabel', { defaultValue: 'Campaign sources' })}>
        {CAMPAIGN_TOP_TAB_IDS.map((tabId) => {
          const isActive = selectedTopTab === tabId;
          if (tabId === 'tutorial') {
            return (
              <button
                key={tabId}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`campaign-book-thumb campaign-book-thumb--asset ${isActive ? 'campaign-book-thumb-active' : ''}`}
                style={{ backgroundImage: 'url(/images/280.jpg)' }}
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  setSelectedTopTab(tabId);
                }}
                aria-label={t('campaign.topTabTutorial', { defaultValue: 'Tutorial missions' })}
              />
            );
          }
          if (tabId === 'anthology') {
            return (
              <button
                key={tabId}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`campaign-book-thumb campaign-book-thumb--anthology ${isActive ? 'campaign-book-thumb-active' : ''}`}
                style={{ backgroundImage: 'url(/images/notebook.png)' }}
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  setSelectedTopTab(tabId);
                }}
                aria-label={t('battleOverview.anthology')}
              />
            );
          }
          return (
            <button
              key={tabId}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`campaign-book-thumb campaign-book-thumb-placeholder ${isActive ? 'campaign-book-thumb-active' : ''}`}
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setSelectedTopTab(tabId);
              }}
              aria-label={t('campaign.topTabComingSoon', { defaultValue: 'Coming soon' })}
            >
              ?
            </button>
          );
        })}
      </div>

      {/* Tutorial missions: separate stage so book-flip layout stays isolated (Photo Orb / David pages) */}
      {selectedTopTab === 'tutorial' ? (
        <div className="campaign-tutorial-stage">
          <div className="campaign-tutorial-panel">
            <div className="tutorial-page-content">
              <div className="tutorial-boxes">
                {TUTORIAL_MISSION_ITEMS.map((item, index) => {
                  const completed = item.missionId ? isTutorialCampaignMissionCompleted(item.missionId) : false;
                  const clickable = Boolean(item.missionId);
                  return (
                    <React.Fragment key={item.id}>
                      {index > 0 && <div className="tutorial-box-connector" aria-hidden="true" />}
                      <div
                        className={`tutorial-box ${clickable ? 'tutorial-box-clickable' : ''} ${completed ? 'tutorial-box-completed' : ''}`}
                        onClick={() => {
                          if (!clickable) return;
                          handleTutorialMissionClick(item);
                        }}
                        role={clickable ? 'button' : undefined}
                      >
                        <div className="tutorial-box-image" style={{ backgroundImage: `url(/images/${item.image})` }} />
                        <h3 className="tutorial-box-title">{t(item.titleKey)}</h3>
                        <p className="tutorial-box-description">{t(item.descriptionKey)}</p>
                        {completed && (
                          <div className="tutorial-box-check" aria-label={t('tutorial.completed')}>
                            ✓
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
      <div className="book-flip-container">
          {selectedTopTab !== 'anthology' ? (
          <div
            className="campaign-storybook-locked"
            role="region"
            aria-label={`${t('campaign.storybook.notAvailableTitle')}. ${t('campaign.storybook.notAvailableBody')}`}
          >
            <div
              className="campaign-storybook-locked-cover"
              style={{ backgroundImage: 'url(/images/notebook.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            <div className="campaign-storybook-locked-message">
              <p className="campaign-storybook-locked-title">{t('campaign.storybook.notAvailableTitle')}</p>
              <p className="campaign-storybook-locked-body">{t('campaign.storybook.notAvailableBody')}</p>
            </div>
          </div>
          ) : isStorybookAvailable ? (
          <>
          <HTMLFlipBook
            ref={flipBookRef}
            width={481}
            height={650}
            maxShadowOpacity={0.5}
            drawShadow={true}
            showCover={true}
            size="fixed"
            className={`campaign-flip-book ${currentPageIndex === 0 ? 'book-closed' : ''}`}
            useMouseEvents={!isRelivePickerActive}
            clickEventForward={false}
            onFlip={handleFlip}
            onFlipStart={handleFlipStart}
          >
            {/* Cover Page */}
            <div className="page">
              <div className="page-content cover" style={{ backgroundImage: 'url(/images/notebook.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              </div>
            </div>

            {/* Inside Cover (blank or title page) */}
            <div className="page">
              <div className="page-content" style={{ 
                padding: '0',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                fontFamily: 'Arial, sans-serif',
                position: 'relative',
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}>
                {/* Decorative background elements */}
                <div style={{
                  position: 'absolute',
                  top: '-20%',
                  right: '-10%',
                  width: '400px',
                  height: '400px',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
                  borderRadius: '50%',
                  zIndex: 0
                }}></div>
                <div style={{
                  position: 'absolute',
                  bottom: '-15%',
                  left: '-8%',
                  width: '350px',
                  height: '350px',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                  borderRadius: '50%',
                  zIndex: 0
                }}></div>

                {/* Main content */}
                <div style={{
                  position: 'relative',
                  zIndex: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '15px 20px',
                  justifyContent: 'space-between'
                }}>
                  {/* Top - Title */}
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '8px',
                    flexShrink: 0
                  }}>
                    <h1 style={{ 
                      fontSize: '2.5em', 
                      color: '#ffffff', 
                      margin: '0',
                      fontFamily: 'Arial, sans-serif',
                      fontWeight: '900',
                      letterSpacing: '1px',
                      lineHeight: '1',
                      textShadow: '3px 3px 6px rgba(0,0,0,0.3)',
                      textTransform: 'uppercase'
                    }}>
                      Photo Orb 2050
                    </h1>
                  </div>

                  {/* Text boxes above orb */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    marginBottom: '10px',
                    flexShrink: 0
                  }}>
                    {/* Left text box */}
                    <div style={{
                      background: 'rgba(255,255,255,0.9)',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                      <p style={{ 
                        fontSize: '0.75em', 
                        color: '#333', 
                        margin: '0',
                        fontFamily: 'Arial, sans-serif',
                        fontWeight: '600',
                        lineHeight: '1.3'
                      }}>
                        <span style={{ 
                          fontSize: '0.95em', 
                          color: '#667eea',
                          fontWeight: '900'
                        }}>{t('campaign.photoOrb.nonInvasive')}</span> {t('campaign.photoOrb.followsSafely')}
                      </p>
                    </div>

                    {/* Right text box */}
                    <div style={{
                      background: 'rgba(255,255,255,0.9)',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                      <p style={{ 
                        fontSize: '0.75em', 
                        color: '#333', 
                        margin: '0',
                        fontFamily: 'Arial, sans-serif',
                        fontWeight: '600',
                        lineHeight: '1.3'
                      }}>
                        {t('campaign.photoOrb.captureAllMoments')}
                      </p>
                    </div>
                  </div>

                  {/* Center - Main Orb Image */}
                  <div style={{
                    flex: '1',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    minHeight: '200px',
                    margin: '5px 0',
                    maxHeight: '240px'
                  }}>
                    <div style={{
                      position: 'relative',
                      padding: '10px',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                    }}>
                      <img 
                        src="/images/photo_drone-removebg-preview.png" 
                        alt="Photo Orb 2050" 
                        style={{
                          maxWidth: '220px',
                          maxHeight: '220px',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.4))'
                        }}
                      />
                    </div>
                  </div>

                  {/* Text box below orb */}
                  <div style={{
                    background: 'rgba(255,255,255,0.9)',
                    padding: '10px 15px',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    marginTop: '10px',
                    marginBottom: '10px',
                    flexShrink: 0,
                    textAlign: 'center'
                  }}>
                    <p style={{ 
                      fontSize: '0.8em', 
                      color: '#333', 
                      margin: '0',
                      fontFamily: 'Arial, sans-serif',
                      fontWeight: '600',
                      lineHeight: '1.4'
                    }}>
                      {t('campaign.photoOrb.logsAutomatically')}
                    </p>
                  </div>

                  {/* People images with quotes */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    alignItems: 'center',
                    flexShrink: 0
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <img 
                        src="/images/happy_man-removebg-preview.png" 
                        alt="Happy moments" 
                        style={{
                          maxWidth: '110px',
                          maxHeight: '110px',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                        }}
                      />
                      <p style={{
                        fontSize: '0.7em',
                        color: '#ffffff',
                        margin: '0',
                        fontFamily: 'Arial, sans-serif',
                        fontStyle: 'italic',
                        fontWeight: '600',
                        textAlign: 'center',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                      }}>
                        {t('campaign.photoOrb.quoteHilarious')}
                      </p>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <img 
                        src="/images/happy_family-removebg-preview.png" 
                        alt="Family moments" 
                        style={{
                          maxWidth: '110px',
                          maxHeight: '110px',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                        }}
                      />
                      <p style={{
                        fontSize: '0.7em',
                        color: '#ffffff',
                        margin: '0',
                        fontFamily: 'Arial, sans-serif',
                        fontStyle: 'italic',
                        fontWeight: '600',
                        textAlign: 'center',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                      }}>
                        {t('campaign.photoOrb.quoteFamily')}
                      </p>
                    </div>
                  </div>

                  {/* Bottom - Branding */}
                  <div style={{
                    textAlign: 'center',
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '2px solid rgba(255,255,255,0.3)',
                    flexShrink: 0
                  }}>
                    <p style={{ 
                      fontSize: '0.7em', 
                      color: '#ffffff', 
                      fontStyle: 'normal',
                      fontFamily: 'Arial, sans-serif',
                      margin: '0',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      fontWeight: '700',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                    }}>
                      {t('campaign.photoOrb.broughtToYou')} <span style={{ 
                        color: '#ffd700',
                        fontWeight: '900',
                        fontSize: '1.05em'
                      }}>{t('campaign.photoOrb.theSentients')}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Pages */}
            {campaignPages.map((page, index) => {
              const isLastPage = index === campaignPages.length - 1;
              const isSecondLastPage = index === campaignPages.length - 2;
              const isLastTwoPages = isLastPage || isSecondLastPage;
              const isFirstPage = index === 0;
              const isPage2 = index === 1; // Page 2 (second content page, index 1)
              const isPage3 = index === 2; // Page 3 (third content page, index 2)
              return (
                <div className="page" key={index}>
                  <div 
                    className={`page-content ${isLastTwoPages ? 'cover' : ''}`} 
                    style={{ position: 'relative' }}
                  >
                    {/* Glued-in photos for first page */}
                    {isFirstPage && (
                      <div className="glued-photos">
                        <img 
                          src="/images/campaign_1_picnic.jpg" 
                          alt="Photo 1" 
                          className="glued-photo photo-1"
                        />
                        <img 
                          src="/images/campaign_2_cheese.jpg" 
                          alt="Photo 2" 
                          className="glued-photo photo-2"
                        />
                      </div>
                    )}
                    {/* Text boxes for first page - inside page structure to turn with page */}
                    {isFirstPage && (
                      <>
                        <div className="look-at-this-textbox">
                          <p>{t('campaign.story.lastDinner')}</p>
                        </div>
                        <div className="below-photo-textbox">
                          <p>{t('campaign.story.sentientsThere')}</p>
                        </div>
                        <p className="handwritten-note">{t('campaign.story.cheeseNote')}</p>
                      </>
                    )}
                    {/* Photo and text for page 2 */}
                    {isPage2 && (
                      <div className="glued-photos">
                        <img 
                          src="/images/campaign_3_david_cornfield.jpg" 
                          alt="David Cornfield" 
                          className="glued-photo photo-3"
                        />
                      </div>
                    )}
                    {/* Text below photo on page 2 */}
                    {isPage2 && (
                      <div className="page2-textbox">
                        <p>{t('campaign.story.infectedCornfield')}</p>
                      </div>
                    )}
                    {isFirstPage && (
                      <div className="page2-bottom-note">
                        <p>{t('campaign.story.carToBarnNote')}</p>
                      </div>
                    )}
                    {/* Photo and text for page 3 */}
                    {isPage3 && (
                      <>
                        <div className="glued-photos">
                          <div className="glued-photo photo-isabela-page3">
                            <img 
                              src="/images/campaign_4_isabela_cornfield.jpg" 
                              alt="Isabela Cornfield" 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </div>
                        </div>
                        <div className="page3-text-below-photo">
                          <p>{t('campaign.story.isabelaShotgun')}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </HTMLFlipBook>
          {reliveOverlaySlot === 3 && (
            <div
              className={`page3-relive-controls page3-relive-controls-overlay campaign-relive-overlay${
                reliveOverlayOpaque
                  ? ' campaign-relive-overlay--opaque'
                  : ' campaign-relive-overlay--transparent'
              }`}
            >
              <select
                className="page3-relive-strategy-select"
                value={selectedRemnantsStrategyId}
                onChange={(e) => setSelectedRemnantsStrategyId(e.target.value)}
                onFocus={() => setIsRelivePickerActive(true)}
                onBlur={() => setIsRelivePickerActive(false)}
                disabled={Boolean(startingReliveMission) || remnantsStrategies.length === 0}
              >
                {remnantsStrategies.length === 0 ? (
                  <option value="">No Remnants strategies available</option>
                ) : (
                  remnantsStrategies.map((strategy) => (
                    <option key={strategy.id} value={String(strategy.id)}>
                      {strategy.name}
                    </option>
                  ))
                )}
              </select>
              <button
                type="button"
                className="page3-relive-button"
                onClick={() => handleReliveAdventure('chapter_1_mission_5')}
                disabled={!selectedRemnantsStrategyId || Boolean(startingReliveMission)}
              >
                {startingReliveMission === 'chapter_1_mission_5' ? 'Starting...' : 'Relive the adventure'}
              </button>
            </div>
          )}
          {reliveOverlaySlot === 5 && (
            <div
              className={`page4-relive-controls page4-relive-controls-overlay campaign-relive-overlay${
                reliveOverlayOpaque
                  ? ' campaign-relive-overlay--opaque'
                  : ' campaign-relive-overlay--transparent'
              }`}
            >
              <select
                className="page3-relive-strategy-select"
                value={selectedRemnantsStrategyId}
                onChange={(e) => setSelectedRemnantsStrategyId(e.target.value)}
                onFocus={() => setIsRelivePickerActive(true)}
                onBlur={() => setIsRelivePickerActive(false)}
                disabled={Boolean(startingReliveMission) || remnantsStrategies.length === 0}
              >
                {remnantsStrategies.length === 0 ? (
                  <option value="">No Remnants strategies available</option>
                ) : (
                  remnantsStrategies.map((strategy) => (
                    <option key={strategy.id} value={String(strategy.id)}>
                      {strategy.name}
                    </option>
                  ))
                )}
              </select>
              <button
                type="button"
                className="page3-relive-button"
                onClick={() => handleReliveAdventure('chapter_1_mission_6')}
                disabled={!selectedRemnantsStrategyId || Boolean(startingReliveMission)}
              >
                {startingReliveMission === 'chapter_1_mission_6' ? 'Starting...' : 'Relive the adventure'}
              </button>
            </div>
          )}
          </>
          ) : (
          <div
            className="campaign-storybook-locked"
            role="region"
            aria-label={`${t('campaign.storybook.notAvailableTitle')}. ${t('campaign.storybook.notAvailableBody')}`}
          >
            <div
              className="campaign-storybook-locked-cover"
              style={{ backgroundImage: 'url(/images/notebook.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            <div className="campaign-storybook-locked-message">
              <p className="campaign-storybook-locked-title">{t('campaign.storybook.notAvailableTitle')}</p>
              <p className="campaign-storybook-locked-body">{t('campaign.storybook.notAvailableBody')}</p>
            </div>
          </div>
          )}
      </div>
      )}
        </div>
      </div>
    </div>
  );
}

export default Campaign;

